/**
 * DOSE waitlist form — vanilla JS port of the interactive bits of the
 * Next.js site's Waitlist() component (app/page.tsx). No React, no
 * sessionStorage bridge: submit adds to Shopify's own cart (name/email/
 * promo/referral ride along as cart line-item properties — see the
 * `properties[...]` input names in sections/dose-hero.liquid) and lands the
 * customer on the product page rather than jumping straight to checkout,
 * so they see it's in their cart before deciding to pay — the device isn't
 * actually reserved until checkout completes. Checkout, order, and refund
 * records are Shopify's from there on.
 *
 * Deliberately NOT ported from the old site: the Web3Forms lead-email POST
 * and its honeypot fields (there's no separate "submit a lead" step to spam
 * anymore — the only submit path is a real Shopify checkout), the custom
 * GA4 Measurement Protocol call (Shopify has its own native analytics/Web
 * Pixels), and the ?checkout=success return-trip success panel (Shopify's
 * own Order Status / Thank You page replaces it — see the plan's open item
 * on Order status page scripting).
 */
;(function () {
  'use strict'

  /* ── Product-page state, driven by the REAL cart (not a ?reserved=1 URL
     param — that seemed simpler, but Shopify's /discount/<code>?redirect=X
     endpoint strips X's own query string when it redirects, so the flag
     never survives a redirect that carries a promo code). Checking
     /cart.js instead is robust regardless of which path got them here, and
     naturally covers a customer returning to this page later too:
       - item_count > 0 → this item is ALREADY in the cart, so: show the
         confirmation banner (worded as "in your cart", NOT "reserved" —
         that's only true once checkout completes), and swap the normal
         add-to-cart-then-checkout form for a plain "Continue to Checkout"
         link so the button can't add a SECOND unit on top.
     Independent of the form guard below since this runs on the PRODUCT
     page, not the homepage. ── */
  const reservedBanner = document.querySelector('[data-reserved-banner]')
  const buyForm = document.querySelector('.product-main-form')
  const continueLink = document.querySelector('[data-continue-checkout]')
  const qtyWrap = document.querySelector('[data-cart-qty-wrap]')
  const qtyInput = document.querySelector('[data-cart-qty]')
  const qtyNote = document.querySelector('[data-cart-qty-note]')
  const qtySteps = [...document.querySelectorAll('[data-qty-step]')]
  let cartLineKey = null
  let qtyBusy = false

  const money = (cents, currency) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100)

  function paintQty(cart) {
    if (!qtyWrap) return
    const line = cart.items && cart.items[0]
    if (!line) {
      // last unit removed — hand the page back to the normal add-to-cart form
      cartLineKey = null
      qtyWrap.hidden = true
      if (reservedBanner) reservedBanner.hidden = true
      if (continueLink) continueLink.hidden = true
      if (buyForm) buyForm.hidden = false
      return
    }
    cartLineKey = line.key
    qtyWrap.hidden = false
    if (qtyInput && document.activeElement !== qtyInput) qtyInput.value = line.quantity
    if (qtyNote) qtyNote.textContent = '' // errors only — no running total
  }

  function setQty(next) {
    if (qtyBusy || !cartLineKey) return
    const quantity = Math.max(0, parseInt(next, 10) || 0)
    qtyBusy = true
    if (qtyNote) qtyNote.textContent = ''
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: cartLineKey, quantity: quantity })
    })
      .then((r) => r.json())
      .then((cart) => paintQty(cart))
      .catch((err) => {
        console.error('[dose-waitlist] cart quantity update failed:', err)
        if (qtyNote) qtyNote.textContent = "Couldn't update quantity. Please try again."
      })
      .finally(() => { qtyBusy = false })
  }

  qtySteps.forEach((btn) => {
    btn.addEventListener('click', () => {
      const delta = parseInt(btn.dataset.qtyStep, 10) || 0
      setQty((parseInt(qtyInput.value, 10) || 0) + delta)
    })
  })
  qtyInput?.addEventListener('change', () => setQty(qtyInput.value))

  if (reservedBanner || qtyWrap) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        if (cart.item_count > 0) {
          if (reservedBanner) reservedBanner.hidden = false
          if (buyForm) buyForm.hidden = true
          if (continueLink) continueLink.hidden = false
          paintQty(cart)
        }
      })
      .catch((err) => console.error('[dose-waitlist] cart state check failed:', err))
  }

  const form = document.getElementById('dose-waitlist-form')
  if (!form) return

  const configEl = document.getElementById('dose-waitlist-config')
  const config = configEl ? JSON.parse(configEl.textContent) : {}
  const promoCodes = (config.promoCodes || []).map((c) => c.toUpperCase())

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  /* ── Spots-remaining counter: real inventory rendered server-side by
     Liquid (data-spots-remaining), animated the same easeOutQuart way the
     old site animated its /api/spots fetch result. No fetch needed here —
     Shopify's inventory count IS the number, already in the page. ── */
  const counterEl = form.closest('.card')?.querySelector('[data-spots-remaining]')
  if (counterEl) {
    const target = Math.max(0, parseInt(counterEl.dataset.spotsRemaining, 10) || 0)
    const ms = 1800
    let start = null
    const tick = (now) => {
      if (start === null) start = now
      const p = Math.min((now - start) / ms, 1)
      const eased = 1 - Math.pow(1 - p, 4)
      counterEl.textContent = Math.floor(eased * target).toLocaleString('en-US')
      if (p < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }


  /* ── Promo apply (cosmetic check, same pattern as the old Stripe site: the
     REAL discount is a Shopify discount code, applied via /discount/<code>
     at checkout — kept in sync manually with promoCodes above). ── */
  const promoInput = form.querySelector('[data-promo-input]')
  const promoApplyBtn = form.querySelector('[data-promo-apply]')
  const promoMsg = form.querySelector('[data-promo-msg]')
  const pbTotal = form.querySelector('[data-pb-total]')
  const pbCompareAt = form.querySelector('[data-pb-compare-at]')
  const pbDiscountRow = form.querySelector('[data-pb-discount-row]')
  const pbDiscount = form.querySelector('[data-pb-discount]')
  let appliedPromoCode = ''

  /* Referral code from URL (?ref / ?referral / ?r) prefills the same optional
     field — there's no separate dedicated referral input anymore. */
  const urlParams = new URLSearchParams(window.location.search)
  const refFromUrl = urlParams.get('ref') || urlParams.get('referral') || urlParams.get('r')
  if (refFromUrl && promoInput && !promoInput.value) promoInput.value = refFromUrl

  function setPromoMsg(text, ok) {
    promoMsg.textContent = text
    promoMsg.className = 'promo-msg' + (text ? (ok ? ' promo-success' : ' promo-error') : '')
  }

  function applyPromoDisplay() {
    if (pbTotal) pbTotal.textContent = config.promoPriceDisplay
    if (pbDiscount) pbDiscount.textContent = config.promoDiscountDisplay
    if (pbDiscountRow) pbDiscountRow.hidden = false
    if (pbCompareAt) {
      if (!pbCompareAt.textContent.trim()) pbCompareAt.textContent = config.compareAtDisplay
      pbCompareAt.hidden = false
    }
  }

  function revertPromoDisplay() {
    if (pbTotal) pbTotal.textContent = pbTotal.dataset.original
    // Only re-hide the discount/compare-at rows if they weren't already
    // showing a REAL Shopify compare-at price before any promo was tried.
    if (pbDiscountRow && pbDiscountRow.dataset.realDiscount !== 'true') pbDiscountRow.hidden = true
    if (pbCompareAt && pbCompareAt.dataset.realDiscount !== 'true') pbCompareAt.hidden = true
  }

  // Remember whether the discount/compare-at rows were server-rendered from
  // a real Shopify compare-at price, so reverting an invalid promo doesn't
  // hide a discount that's actually real.
  if (pbDiscountRow) pbDiscountRow.dataset.realDiscount = pbDiscountRow.hidden ? 'false' : 'true'
  if (pbCompareAt) pbCompareAt.dataset.realDiscount = pbCompareAt.hidden ? 'false' : 'true'

  function applyPromo() {
    const raw = (promoInput.value || '').trim().toUpperCase()
    if (!raw) {
      setPromoMsg('Enter a code first.', false)
      return
    }
    if (promoCodes.includes(raw)) {
      appliedPromoCode = raw
      setPromoMsg('✓ Code applied — additional discount added', true)
      applyPromoDisplay()
    } else {
      appliedPromoCode = ''
      setPromoMsg('Invalid code. Please check and try again.', false)
      revertPromoDisplay()
    }
  }

  promoApplyBtn?.addEventListener('click', applyPromo)
  promoInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      applyPromo()
    }
  })

  /* ── Consent gates the submit button, exactly like the old site ── */
  const consentCheck = form.querySelector('[data-consent-check]')
  const submitBtn = form.querySelector('[data-submit-btn]')
  consentCheck?.addEventListener('change', () => {
    submitBtn.disabled = !consentCheck.checked
    submitBtn.title = consentCheck.checked ? '' : 'Please agree to the terms above to continue'
  })

  /* ── Submit: validate, add to cart via the Ajax Cart API, then go straight
     to checkout — through /discount/<code> first if a promo validated. ── */
  const formError = form.querySelector('[data-form-error]')
  const submitLabel = form.querySelector('[data-submit-label]')
  const firstNameInput = document.getElementById('fn')
  const lastNameInput = document.getElementById('ln')
  const emailInput = document.getElementById('em')

  function showFormError(msg) {
    formError.textContent = msg
    formError.hidden = false
  }

  const submitLabelIdle = submitLabel.innerHTML

  function setSubmitting(state) {
    submitBtn.disabled = state || !consentCheck.checked
    submitLabel.innerHTML = state
      ? '<svg width="13" height="13" viewBox="0 0 15 15" fill="none" style="animation:spin .8s linear infinite">' +
        '<circle cx="7.5" cy="7.5" r="5.5" stroke="rgba(255,255,255,0.22)" stroke-width="2" />' +
        '<path d="M13 7.5A5.5 5.5 0 0 1 2 7.5" stroke="white" stroke-width="2" stroke-linecap="round" /></svg>' +
        'Securing your spot…'
      : submitLabelIdle
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    formError.hidden = true

    if (!firstNameInput.value.trim() || !lastNameInput.value.trim()) {
      showFormError('Please enter your first and last name.')
      return
    }
    if (!isValidEmail(emailInput.value.trim())) {
      showFormError('Please enter a valid email address.')
      return
    }
    if (!consentCheck.checked) return // button is disabled in this state; belt and braces

    setSubmitting(true)
    try {
      /* One reservation at a time.

         /cart/add.js APPENDS. This panel has no "already reserved" guard (the
         product page does, which is why the leak only shows up here), so going
         back and reserving again stacked a second line — either the same device
         twice or one of each. The quantity stepper on the product page binds to
         cart.items[0] only, so that second line had no control bound to it and
         could not be removed.

         Clearing first makes the cart mirror the panel: whatever was chosen
         last is what is reserved. Non-fatal if it fails — a failed clear
         degrades to the old append rather than blocking the reservation. */
      try {
        await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { Accept: 'application/json' }
        })
      } catch (clearErr) {
        console.warn('[dose-waitlist] cart clear failed, adding anyway:', clearErr)
      }

      const body = new FormData(form)
      const res = await fetch(config.cartAddUrl + '.js', { method: 'POST', body })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.description || 'Could not add to cart')
      }
      /* Reserve → product page → checkout. The product page is where quantity
         can still be changed (Shopify's checkout won't allow it), so it earns
         its place in the flow again.

         Which product depends on the device picker, so read the selected tab
         from the DOM rather than duplicating that state here — the switcher
         owns it, and this only needs the answer at submit time. Falls back to
         checkout if a product URL is missing (e.g. a draft product). */
      const selectedTab = document.querySelector('[data-device-tab][aria-selected="true"]')
      const selectedKey = selectedTab ? selectedTab.dataset.deviceTab : 'base'
      const selectedDevice = (config.devices || {})[selectedKey] || {}
      const target = selectedDevice.url || config.checkoutUrl
      const dest = appliedPromoCode
        ? `/discount/${encodeURIComponent(appliedPromoCode)}?redirect=${encodeURIComponent(target)}`
        : target
      window.location.assign(dest)
    } catch (err) {
      setSubmitting(false)
      showFormError('Something went wrong reserving your spot. Please try again.')
      console.error('[dose-waitlist] add to cart failed:', err)
    }
  })
})()

/**
 * Scroll-reveal for grouped entrance animations (the feature pills beside the
 * device render). The animation used to run on page load, which meant the
 * staggered sequence had almost always finished before that section was
 * scrolled into view — nobody actually saw it.
 *
 * The `will-animate` class is added here rather than baked into the CSS so the
 * hidden state depends on JS being alive: if this script never runs, or the
 * browser has no IntersectionObserver, or the visitor prefers reduced motion,
 * the pills simply render visible instead of being stranded at opacity 0.
 */
;(function () {
  const groups = Array.from(document.querySelectorAll('[data-reveal-group]'))
  if (!groups.length) return

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reducedMotion) return

  groups.forEach((group) => group.classList.add('will-animate'))

  const pending = new Set(groups)
  let observer = null

  function teardown() {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    if (observer) observer.disconnect()
  }

  function reveal(group) {
    if (!pending.has(group)) return // one-shot: don't replay on scroll-back
    pending.delete(group)
    group.classList.add('is-visible')
    if (!pending.size) teardown()
  }

  function inView(el) {
    const rect = el.getBoundingClientRect()
    const vh = window.innerHeight || document.documentElement.clientHeight
    return rect.top < vh * 0.85 && rect.bottom > 0
  }

  function checkPending() {
    // copy first: reveal() mutates `pending`, and the last removal tears down
    Array.from(pending).forEach((group) => {
      if (inView(group)) reveal(group)
    })
  }

  let ticking = false
  function onScroll() {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      ticking = false
      checkPending()
    })
  }

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && reveal(entry.target)),
      { threshold: 0.25 }
    )
    groups.forEach((group) => observer.observe(group))
  }

  /* Scroll/resize fallback alongside the observer. The observer is the cheap
     path, but its callback is suspended in some contexts (background tabs,
     certain in-app webviews) — and if it never fires, `will-animate` would
     leave these permanently invisible. A geometry check on scroll costs
     little and removes that failure mode entirely. Both funnel through
     reveal(), which is idempotent, so whichever wins the race is fine. */
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll)
  checkPending() // synchronous, so anything already in view reveals without
                 // waiting on a frame callback that may never be scheduled
})()

/**
 * Device switcher — DOSE Device vs DOSE Device PRO.
 *
 * There are two pickers on the page (tabs above the feature list, and the one
 * inside the buy panel) but only ONE piece of state. Every control carries
 * [data-device-tab], every control re-renders from that single state, so which
 * one you click is irrelevant — they can't drift apart because neither owns the
 * value. Adding a third picker later needs no JS changes.
 */
;(function () {
  const configEl = document.getElementById('dose-waitlist-config')
  if (!configEl) return

  let config = {}
  try { config = JSON.parse(configEl.textContent) } catch (e) { return }
  const devices = config.devices
  if (!devices || !devices.base || !devices.pro) return

  const tabs = [...document.querySelectorAll('[data-device-tab]')]
  if (!tabs.length) return

  const variantInput = document.querySelector('[data-variant-input]')
  const featureList = document.querySelector('.fd-list')
  const featureRows = [...document.querySelectorAll('.fd-list [data-device]')]
  const eyebrowBadge = document.querySelector('[data-device-badge]')
  const nameLabels = [...document.querySelectorAll('[data-device-name]')]
  const lineLabels = [...document.querySelectorAll('[data-pb-device-name]')]
  const pbTotal = document.querySelector('[data-pb-total]')
  const pbCompareAt = document.querySelector('[data-pb-compare-at]')
  const pbDiscountRow = document.querySelector('[data-pb-discount-row]')
  const pbDiscount = document.querySelector('[data-pb-discount]')

  let current = null

  function render(key) {
    const device = devices[key]
    if (!device || key === current) return
    current = key

    tabs.forEach((tab) => {
      const on = tab.dataset.deviceTab === key
      tab.setAttribute('aria-selected', String(on))
      tab.classList.toggle('is-active', on)
    })

    // 'both' always shows; 'base'/'pro' rows show only for their own device
    featureRows.forEach((row) => {
      const only = row.dataset.device
      row.hidden = only !== 'both' && only !== key
    })

    if (eyebrowBadge) eyebrowBadge.hidden = key !== 'pro'

    nameLabels.forEach((el) => { el.textContent = device.label })
    // no badge alongside the price-breakdown label, so spell PRO out there
    lineLabels.forEach((el) => {
      el.textContent = key === 'pro' ? device.label + ' PRO' : device.label
    })

    if (variantInput && device.variantId) variantInput.value = device.variantId

    // Keep the price breakdown honest — it's server-rendered for one device only.
    if (pbTotal) {
      pbTotal.textContent = device.price
      pbTotal.dataset.original = device.price
    }
    if (pbCompareAt) {
      pbCompareAt.textContent = device.compareAt || ''
      pbCompareAt.hidden = !device.hasCompareAt
      pbCompareAt.dataset.realDiscount = device.hasCompareAt ? 'true' : 'false'
    }
    if (pbDiscountRow) {
      pbDiscountRow.hidden = !device.hasCompareAt
      pbDiscountRow.dataset.realDiscount = device.hasCompareAt ? 'true' : 'false'
    }
    if (pbDiscount && device.hasCompareAt) pbDiscount.textContent = '−' + device.discount

    document.dispatchEvent(new CustomEvent('dose:device-change', { detail: { device: key } }))
  }

  /* The copy column is vertically centred, so a shorter list would re-centre
     the whole thing and visibly shift the heading and subtext. Measure the
     tallest variant once and pin that as a floor, so switching only swaps rows
     inside a box whose height never changes. Measured rather than hardcoded so
     it stays correct when the copy is edited in the theme editor. */
  function lockListHeight() {
    if (!featureList) return
    let tallest = 0
    ;['base', 'pro'].forEach((key) => {
      featureRows.forEach((row) => {
        const only = row.dataset.device
        row.hidden = only !== 'both' && only !== key
      })
      tallest = Math.max(tallest, featureList.getBoundingClientRect().height)
    })
    featureList.style.minHeight = Math.ceil(tallest) + 'px'
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => render(tab.dataset.deviceTab))
  })

  lockListHeight()
  render(config.defaultDevice === 'pro' ? 'pro' : 'base')
  // fonts land after first paint and change row heights — re-measure once
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      featureList.style.minHeight = ''
      const active = current
      current = null
      lockListHeight()
      render(active)
    })
  }
  addEventListener('resize', () => {
    if (!featureList) return
    featureList.style.minHeight = ''
    const active = current
    current = null
    lockListHeight()
    render(active)
  })
})()

/**
 * Where "Join the Waitlist" lands.
 *
 * The link points at #waitlist, and a plain hash jump aligns that element's top
 * with the viewport top. But the hero runs ~1000px against a ~750px laptop
 * viewport, so that landing leaves the end of the feature list — the PRO rows,
 * Bottle Volume last — a few hundred pixels below the fold. Someone who clicked
 * through specifically to compare devices arrives at a list they cannot see the
 * bottom of, with nothing on screen suggesting there is more.
 *
 * Note the list is always PRO-height: lockListHeight() above pins it to the
 * taller variant, so this is not a PRO-only problem, it is just most visible
 * there because the cropped rows are the PRO ones.
 *
 * So compute the landing rather than letting the hash pick it. Two constraints:
 *
 *   1. don't scroll past the buy card's top clearing the sticky nav — landing
 *      in the middle of a form is its own kind of broken
 *   2. do scroll far enough that the last feature row is on screen
 *
 * On a tall viewport both hold and (1) wins, which looks exactly like today.
 * On a short one they conflict and (2) wins: you lose the top of the card and
 * keep the whole list. That trade is the entire point of this block — if it
 * ever needs reversing, swap Math.max for Math.min and the old behaviour is
 * back.
 */
;(function () {
  const links = [...document.querySelectorAll('a[href="#waitlist"]')]
  if (!links.length) return

  const GAP = 24

  function landing() {
    const list = document.querySelector('.fd-list')
    const card = document.querySelector('#waitlist .card')
    if (!list || !card) return null

    const nav = document.querySelector('nav')
    const navH = nav ? nav.getBoundingClientRect().height : 65
    const cardTop = card.getBoundingClientRect().top + window.scrollY - navH - GAP
    const listEnd = list.getBoundingClientRect().bottom + window.scrollY + GAP - window.innerHeight

    return Math.max(0, Math.max(cardTop, listEnd))
  }

  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      const y = landing()
      if (y === null) return // markup moved — let the hash do whatever it does
      e.preventDefault()
      window.scrollTo({ top: y, behavior: 'smooth' })
      // replaceState, not location.hash: setting the hash would re-trigger the
      // jump we just overrode, and pushState would litter the back button.
      history.replaceState(null, '', '#waitlist')
    })
  })
})()
